/**
 * SyncStatusBadge Component
 * 
 * Muestra el estado de sincronización con Deenex Multi
 */

import { CheckCircle, AlertCircle, RefreshCw, AlertTriangle } from 'lucide-react';

interface SyncStatusBadgeProps {
    status: 'connected' | 'error' | 'syncing' | 'warning';
    lastSync?: string;
    className?: string;
}

export default function SyncStatusBadge({ status, lastSync, className = '' }: SyncStatusBadgeProps) {
    const getStatusContent = () => {
        switch (status) {
            case 'connected':
                return {
                    icon: <CheckCircle size={14} />,
                    text: 'Sincronizado con Deenex Multi',
                    bgColor: 'bg-green-50',
                    textColor: 'text-green-700',
                    borderColor: 'border-green-200'
                };
            case 'syncing':
                return {
                    icon: <RefreshCw size={14} className="animate-spin" />,
                    text: 'Sincronizando...',
                    bgColor: 'bg-blue-50',
                    textColor: 'text-blue-700',
                    borderColor: 'border-blue-200'
                };
            case 'warning':
                return {
                    icon: <AlertTriangle size={14} />,
                    text: 'Conectado · sin datos todavía',
                    bgColor: 'bg-amber-50',
                    textColor: 'text-amber-700',
                    borderColor: 'border-amber-200'
                };
            case 'error':
                return {
                    icon: <AlertCircle size={14} />,
                    text: 'Error de sincronización',
                    bgColor: 'bg-red-50',
                    textColor: 'text-red-700',
                    borderColor: 'border-red-200'
                };
        }
    };

    const content = getStatusContent();

    return (
        <div
            className={`
                flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
                border ${content.bgColor} ${content.textColor} ${content.borderColor}
                ${className}
            `}
        >
            {content.icon}
            <span>{content.text}</span>
            {lastSync && status === 'connected' && (
                <span className="text-green-600/70 ml-1">
                    · Hace {formatTimeAgo(lastSync)}
                </span>
            )}
        </div>
    );
}

function formatTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'segundos';
    if (diffMins < 60) return `${diffMins} min`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d`;
}
